/**
 * DAWComponents.jsx - Enhanced DAW Studio Components
 * 
 * Full-featured audio workstation with:
 * - Multi-track editing
 * - Effect processing
 * - Beat generation
 * - Loop machine
 * - Auto-tune
 * - Waveform visualization
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  useAudioEditor,
  useAudioPlayback,
  useWaveform,
  useEffectProcessor,
  useLoopMachine,
  useBeatGenerator,
  useAudioAnalysis,
  useRegionSelection,
  useDAWKeyboardShortcuts,
  useMultitrack,
  useAudioRecorder
} from '../utils/audioEditorHooks';
import {
  AUTOTUNE_COLORS,
  SCALES,
  BEAT_PATTERNS,
  FADE_CURVES,
  EFFECT_PRESETS,
  FILTER_TYPES,
  getAudioContext,
  ensureAudioContextRunning,
  mixBuffers
} from '../utils/audioFP';
import { AddChannelWizard } from './AddChannelWizard';
import { TrackWaveform, MidiPianoRollMini } from './TrackWaveform';
import { DAWTimeline } from './DAWTimeline';
import { DAWMixer } from './DAWMixer';
import audioUploadService from '../utils/audioUploadService';
import api from '../services/api';
import PublishModal from './PublishModal';

// =============================================================================
// WAVEFORM DISPLAY COMPONENT
// =============================================================================

export const WaveformDisplay = ({ 
  waveformData, 
  currentTime, 
  duration, 
  selection,
  onSeek,
  onSelectionStart,
  onSelectionUpdate,
  onSelectionEnd,
  height = 120,
  color = '#8b5cf6'
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
  // Handle resize
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };

    updateDimensions();
    const observer = new ResizeObserver(updateDimensions);
    observer.observe(containerRef.current);
    
    return () => observer.disconnect();
  }, []);
  
  // Draw waveform
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !waveformData || dimensions.width === 0) return;
    
    // Set canvas resolution to match display size
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    
    const ctx = canvas.getContext('2d');
    const { width, height: h } = dimensions;
    
    ctx.clearRect(0, 0, width, h);
    
    // Draw selection region
    if (selection) {
      const startX = (selection.start / duration) * width;
      const endX = (selection.end / duration) * width;
      ctx.fillStyle = 'rgba(139, 92, 246, 0.2)';
      ctx.fillRect(startX, 0, endX - startX, h);
    }
    
    // Draw waveform
    ctx.fillStyle = color;
    // Adjust bar width based on zoom/resolution
    // If we have more pixels than data points, we stretch.
    // If we have more data points than pixels, we might want to downsample, but simple drawing is fine for now.
    const barWidth = width / waveformData.length;
    
    for (let i = 0; i < waveformData.length; i++) {
      const amplitude = waveformData[i];
      const barHeight = amplitude * h * 0.8;
      const x = i * barWidth;
      const y = (h - barHeight) / 2;
      
      // Ensure bars are at least 1px wide if possible, or just draw rects
      ctx.fillRect(x, y, Math.max(1, barWidth), barHeight);
    }
    
    // Draw playhead
    const playheadX = (currentTime / duration) * width;
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(playheadX, 0);
    ctx.lineTo(playheadX, h);
    ctx.stroke();
  }, [waveformData, currentTime, duration, selection, color, dimensions]);
  
  // Handle clicks
  const handleClick = useCallback((e) => {
    if (!containerRef.current || !duration) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const time = (x / rect.width) * duration;
    
    onSeek?.(time);
  }, [duration, onSeek]);
  
  // Handle selection drag
  const handleMouseDown = useCallback((e) => {
    if (!containerRef.current || !duration) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const time = (x / rect.width) * duration;
    
    onSelectionStart?.(time);
  }, [duration, onSelectionStart]);
  
  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current || !duration) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const time = Math.max(0, Math.min(duration, (x / rect.width) * duration));
    
    onSelectionUpdate?.(time);
  }, [duration, onSelectionUpdate]);
  
  return (
    <div 
      ref={containerRef}
      className="relative cursor-pointer bg-gray-900 rounded-lg overflow-hidden"
      style={{ height }}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={onSelectionEnd}
      onMouseLeave={onSelectionEnd}
    >
      <canvas 
        ref={canvasRef}
        className="w-full h-full"
      />
      
      {/* Time display */}
      <div className="absolute bottom-1 left-2 text-xs text-gray-400">
        {formatTime(currentTime)} / {formatTime(duration)}
      </div>
    </div>
  );
};

// =============================================================================
// EFFECT RACK COMPONENT
// =============================================================================

export const EffectRack = ({ processor, onApply }) => {
  const [activeTab, setActiveTab] = useState('tempo');
  const [params, setParams] = useState({
    // Tempo
    stretchFactor: 1,
    speedFactor: 1,
    
    // Pitch
    semitones: 0,
    autotuneScale: 'chromatic',
    autotuneRoot: 0,
    autotuneColor: 'natural',
    
    // Reverb
    roomSize: 0.5,
    damping: 0.5,
    reverbMix: 0.3,
    
    // Delay
    delayTime: 0.3,
    feedback: 0.3,
    delayMix: 0.3,
    
    // Filter
    filterType: 'lowpass',
    filterFreq: 1000,
    filterQ: 1,
    
    // Dynamics
    threshold: 0.5,
    ratio: 4,
    attack: 0.01,
    release: 0.1,
    makeupGain: 1,
    
    // Noise
    noiseThreshold: 0.02,
    smoothing: 0.5,
    
    // Gain
    gainDb: 0,
    
    // Fade
    fadeDuration: 1,
    fadeCurve: 'linear'
  });
  
  const updateParam = (key, value) => setParams(p => ({ ...p, [key]: value }));
  
  const TABS = [
    { id: 'tempo', label: '⏱️ Tempo', icon: '⏱️' },
    { id: 'pitch', label: '🎵 Pitch', icon: '🎵' },
    { id: 'autotune', label: '🎤 Auto-Tune', icon: '🎤' },
    { id: 'reverb', label: '🏛️ Reverb', icon: '🏛️' },
    { id: 'delay', label: '📢 Delay', icon: '📢' },
    { id: 'filter', label: '🎛️ Filter', icon: '🎛️' },
    { id: 'dynamics', label: '📊 Dynamics', icon: '📊' },
    { id: 'noise', label: '🔇 Noise', icon: '🔇' },
    { id: 'vocal', label: '🗣️ Vocal', icon: '🗣️' },
    { id: 'fade', label: '📈 Fade', icon: '📈' },
    { id: 'presets', label: '⭐ Presets', icon: '⭐' }
  ];
  
  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      {/* Tab navigation */}
      <div className="flex overflow-x-auto border-b border-gray-700">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-xs whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'bg-purple-600 text-white' 
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            {tab.icon} {tab.label.split(' ')[1]}
          </button>
        ))}
      </div>
      
      {/* Tab content */}
      <div className="p-4 space-y-4">
        {activeTab === 'tempo' && (
          <>
            <EffectSlider
              label="Time Stretch"
              value={params.stretchFactor}
              min={0.5}
              max={2}
              step={0.01}
              onChange={(v) => updateParam('stretchFactor', v)}
              format={(v) => `${(v * 100).toFixed(0)}%`}
            />
            <button
              onClick={() => processor?.stretch(params.stretchFactor)}
              className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
            >
              Apply Time Stretch
            </button>
            
            <div className="border-t border-gray-700 pt-4 mt-4" />
            
            <EffectSlider
              label="Speed (affects pitch)"
              value={params.speedFactor}
              min={0.25}
              max={4}
              step={0.01}
              onChange={(v) => updateParam('speedFactor', v)}
              format={(v) => `${v.toFixed(2)}x`}
            />
            <button
              onClick={() => processor?.speed(params.speedFactor)}
              className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
            >
              Apply Speed Change
            </button>
          </>
        )}
        
        {activeTab === 'pitch' && (
          <>
            <EffectSlider
              label="Pitch Shift (semitones)"
              value={params.semitones}
              min={-12}
              max={12}
              step={1}
              onChange={(v) => updateParam('semitones', v)}
              format={(v) => `${v > 0 ? '+' : ''}${v}`}
            />
            <button
              onClick={() => processor?.pitch(params.semitones)}
              className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
            >
              Apply Pitch Shift
            </button>
          </>
        )}
        
        {activeTab === 'autotune' && (
          <>
            <div className="space-y-2">
              <label className="text-xs text-gray-400">Scale</label>
              <select
                value={params.autotuneScale}
                onChange={(e) => updateParam('autotuneScale', e.target.value)}
                className="w-full bg-gray-700 rounded px-3 py-2 text-sm"
              >
                {Object.keys(SCALES).map(scale => (
                  <option key={scale} value={scale}>{scale}</option>
                ))}
              </select>
            </div>
            
            <EffectSlider
              label="Root Note (C = 0)"
              value={params.autotuneRoot}
              min={0}
              max={11}
              step={1}
              onChange={(v) => updateParam('autotuneRoot', v)}
              format={(v) => ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'][v]}
            />
            
            <div className="space-y-2">
              <label className="text-xs text-gray-400">Auto-Tune Color</label>
              <div className="grid grid-cols-5 gap-1">
                {Object.keys(AUTOTUNE_COLORS).map(color => (
                  <button
                    key={color}
                    onClick={() => updateParam('autotuneColor', color)}
                    className={`px-2 py-1 text-xs rounded capitalize transition-colors ${
                      params.autotuneColor === color
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
            
            <button
              onClick={() => processor?.tune(params.autotuneScale, params.autotuneRoot, params.autotuneColor)}
              className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
            >
              Apply Auto-Tune
            </button>
          </>
        )}
        
        {activeTab === 'reverb' && (
          <>
            <EffectSlider
              label="Room Size"
              value={params.roomSize}
              min={0.1}
              max={1}
              step={0.01}
              onChange={(v) => updateParam('roomSize', v)}
            />
            <EffectSlider
              label="Damping"
              value={params.damping}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => updateParam('damping', v)}
            />
            <EffectSlider
              label="Wet Mix"
              value={params.reverbMix}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => updateParam('reverbMix', v)}
            />
            <button
              onClick={() => processor?.reverb(params.roomSize, params.damping, params.reverbMix)}
              className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
            >
              Apply Reverb
            </button>
          </>
        )}
        
        {activeTab === 'delay' && (
          <>
            <EffectSlider
              label="Delay Time"
              value={params.delayTime}
              min={0.01}
              max={1}
              step={0.01}
              onChange={(v) => updateParam('delayTime', v)}
              format={(v) => `${(v * 1000).toFixed(0)}ms`}
            />
            <EffectSlider
              label="Feedback"
              value={params.feedback}
              min={0}
              max={0.9}
              step={0.01}
              onChange={(v) => updateParam('feedback', v)}
            />
            <EffectSlider
              label="Wet Mix"
              value={params.delayMix}
              min={0}
              max={1}
              step={0.01}
              onChange={(v) => updateParam('delayMix', v)}
            />
            <button
              onClick={() => processor?.delay(params.delayTime, params.feedback, params.delayMix)}
              className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
            >
              Apply Delay
            </button>
          </>
        )}
        
        {activeTab === 'filter' && (
          <>
            <div className="space-y-2">
              <label className="text-xs text-gray-400">Filter Type</label>
              <select
                value={params.filterType}
                onChange={(e) => updateParam('filterType', e.target.value)}
                className="w-full bg-gray-700 rounded px-3 py-2 text-sm"
              >
                {Object.keys(FILTER_TYPES).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <EffectSlider
              label="Frequency"
              value={params.filterFreq}
              min={20}
              max={20000}
              step={10}
              onChange={(v) => updateParam('filterFreq', v)}
              format={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}kHz` : `${v}Hz`}
            />
            <EffectSlider
              label="Q (Resonance)"
              value={params.filterQ}
              min={0.1}
              max={20}
              step={0.1}
              onChange={(v) => updateParam('filterQ', v)}
            />
            <button
              onClick={() => processor?.filter(params.filterType, params.filterFreq, params.filterQ, 0)}
              className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
            >
              Apply Filter
            </button>
          </>
        )}
        
        {activeTab === 'dynamics' && (
          <>
            <EffectSlider
              label="Threshold"
              value={params.threshold}
              min={0.01}
              max={1}
              step={0.01}
              onChange={(v) => updateParam('threshold', v)}
              format={(v) => `${(20 * Math.log10(v)).toFixed(1)}dB`}
            />
            <EffectSlider
              label="Ratio"
              value={params.ratio}
              min={1}
              max={20}
              step={0.5}
              onChange={(v) => updateParam('ratio', v)}
              format={(v) => `${v}:1`}
            />
            <EffectSlider
              label="Attack"
              value={params.attack}
              min={0.001}
              max={0.5}
              step={0.001}
              onChange={(v) => updateParam('attack', v)}
              format={(v) => `${(v * 1000).toFixed(0)}ms`}
            />
            <EffectSlider
              label="Release"
              value={params.release}
              min={0.01}
              max={1}
              step={0.01}
              onChange={(v) => updateParam('release', v)}
              format={(v) => `${(v * 1000).toFixed(0)}ms`}
            />
            <EffectSlider
              label="Makeup Gain"
              value={params.makeupGain}
              min={0.5}
              max={4}
              step={0.1}
              onChange={(v) => updateParam('makeupGain', v)}
              format={(v) => `${v.toFixed(1)}x`}
            />
            <button
              onClick={() => processor?.compress(params.threshold, params.ratio, params.attack, params.release, params.makeupGain)}
              className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
            >
              Apply Compression
            </button>
            
            <div className="border-t border-gray-700 pt-4 mt-4" />
            
            <EffectSlider
              label="Gain (dB)"
              value={params.gainDb}
              min={-24}
              max={24}
              step={0.5}
              onChange={(v) => updateParam('gainDb', v)}
              format={(v) => `${v > 0 ? '+' : ''}${v}dB`}
            />
            <button
              onClick={() => processor?.gain(params.gainDb)}
              className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
            >
              Apply Gain
            </button>
            
            <button
              onClick={() => processor?.norm(-1)}
              className="w-full py-2 bg-gray-700 rounded hover:bg-gray-600 text-sm font-medium"
            >
              Normalize to -1dB
            </button>
          </>
        )}
        
        {activeTab === 'noise' && (
          <>
            <EffectSlider
              label="Noise Threshold"
              value={params.noiseThreshold}
              min={0.001}
              max={0.1}
              step={0.001}
              onChange={(v) => updateParam('noiseThreshold', v)}
              format={(v) => `${(v * 100).toFixed(1)}%`}
            />
            <EffectSlider
              label="Smoothing"
              value={params.smoothing}
              min={0}
              max={0.99}
              step={0.01}
              onChange={(v) => updateParam('smoothing', v)}
            />
            <button
              onClick={() => processor?.noiseReduce(params.noiseThreshold, params.smoothing)}
              className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
            >
              Apply Noise Reduction
            </button>
            
            <div className="border-t border-gray-700 pt-4 mt-4" />
            
            <EffectSlider
              label="Gate Threshold"
              value={params.threshold}
              min={0.001}
              max={0.5}
              step={0.001}
              onChange={(v) => updateParam('threshold', v)}
            />
            <button
              onClick={() => processor?.noiseGate(params.threshold, params.attack, params.release)}
              className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
            >
              Apply Noise Gate
            </button>
          </>
        )}
        
        {activeTab === 'vocal' && (
          <>
            <p className="text-xs text-gray-400 mb-4">
              Vocal processing works best with stereo audio. These effects extract or remove the center channel where vocals typically reside.
            </p>
            <button
              onClick={() => processor?.isolateVocals()}
              className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium mb-2"
            >
              🎤 Extract Vocals (Center)
            </button>
            <button
              onClick={() => processor?.removeVox()}
              className="w-full py-2 bg-gray-700 rounded hover:bg-gray-600 text-sm font-medium"
            >
              🎵 Remove Vocals (Instrumental)
            </button>
          </>
        )}
        
        {activeTab === 'fade' && (
          <>
            <EffectSlider
              label="Fade Duration"
              value={params.fadeDuration}
              min={0.1}
              max={10}
              step={0.1}
              onChange={(v) => updateParam('fadeDuration', v)}
              format={(v) => `${v.toFixed(1)}s`}
            />
            <div className="space-y-2">
              <label className="text-xs text-gray-400">Fade Curve</label>
              <div className="grid grid-cols-5 gap-1">
                {Object.keys(FADE_CURVES).map(curve => (
                  <button
                    key={curve}
                    onClick={() => updateParam('fadeCurve', curve)}
                    className={`px-2 py-1 text-xs rounded capitalize transition-colors ${
                      params.fadeCurve === curve
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {curve}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button
                onClick={() => processor?.fadeIn(params.fadeDuration, params.fadeCurve)}
                className="py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
              >
                📈 Fade In
              </button>
              <button
                onClick={() => processor?.fadeOut(params.fadeDuration, params.fadeCurve)}
                className="py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
              >
                📉 Fade Out
              </button>
            </div>
          </>
        )}
        
        {activeTab === 'presets' && (
          <>
            <p className="text-xs text-gray-400 mb-4">
              Quick effect chains for common scenarios
            </p>
            {processor?.presets.map(preset => (
              <button
                key={preset}
                onClick={() => processor?.applyPreset(preset)}
                className="w-full py-2 bg-gray-700 rounded hover:bg-gray-600 text-sm font-medium mb-2 capitalize"
              >
                {preset.replace(/([A-Z])/g, ' $1').trim()}
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// BEAT MACHINE COMPONENT
// =============================================================================

export const BeatMachine = ({ onBeatGenerated }) => {
  const beats = useBeatGenerator();
  
  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
        🥁 Beat Machine
      </h3>
      
      {/* BPM and Bars */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs text-gray-400">BPM</label>
          <input
            type="number"
            value={beats.bpm}
            onChange={(e) => beats.setBpm(parseInt(e.target.value) || 120)}
            className="w-full bg-gray-700 rounded px-3 py-2 text-sm"
            min={60}
            max={200}
          />
        </div>
        <div>
          <label className="text-xs text-gray-400">Bars</label>
          <input
            type="number"
            value={beats.bars}
            onChange={(e) => beats.setBars(parseInt(e.target.value) || 4)}
            className="w-full bg-gray-700 rounded px-3 py-2 text-sm"
            min={1}
            max={16}
          />
        </div>
      </div>
      
      {/* Pattern selector */}
      <div className="mb-4">
        <label className="text-xs text-gray-400">Pattern</label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          {beats.patterns.map(pattern => (
            <button
              key={pattern}
              onClick={() => beats.setCurrentPattern(pattern)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                beats.currentPattern === pattern
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {pattern}
            </button>
          ))}
        </div>
      </div>
      
      {/* Custom pattern grid */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-gray-400">Custom Pattern</label>
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.localStorage.setItem('daw_pattern_clipboard', JSON.stringify(beats.customPattern));
                  // You might want to add a toast notification here
                }
              }}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Copy
            </button>
            <button
              onClick={() => {
                if (typeof window !== 'undefined') {
                  const saved = window.localStorage.getItem('daw_pattern_clipboard');
                  if (saved && beats.setCustomPattern) {
                    try {
                      beats.setCustomPattern(JSON.parse(saved));
                    } catch (e) {
                      console.error('Failed to paste pattern', e);
                    }
                  }
                }
              }}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Paste
            </button>
            <button
              onClick={() => beats.loadPresetToCustom(beats.currentPattern)}
              className="text-xs text-purple-400 hover:text-purple-300"
            >
              Load from preset
            </button>
          </div>
        </div>
        
        {['kick', 'snare', 'hihat'].map(instrument => (
          <div key={instrument} className="flex items-center gap-1 mb-1">
            <span className="text-xs w-12 text-gray-400 capitalize">{instrument}</span>
            <div className="flex gap-0.5">
              {beats.customPattern[instrument].map((active, step) => (
                <button
                  key={step}
                  onClick={() => beats.toggleStep(instrument, step)}
                  className={`w-5 h-5 rounded text-xs transition-colors ${
                    active
                      ? instrument === 'kick' ? 'bg-red-500' 
                        : instrument === 'snare' ? 'bg-yellow-500'
                        : 'bg-blue-500'
                      : 'bg-gray-700 hover:bg-gray-600'
                  } ${step % 4 === 0 ? 'ml-1' : ''}`}
                />
              ))}
            </div>
          </div>
        ))}
        
        <button
          onClick={beats.clearCustomPattern}
          className="text-xs text-gray-500 hover:text-gray-400 mt-2"
        >
          Clear pattern
        </button>
      </div>
      
      {/* Generate buttons */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={async () => {
            await ensureAudioContextRunning();
            const beat = beats.generateCustomBeat();
            const ctx = getAudioContext();
            const source = ctx.createBufferSource();
            source.buffer = beat;
            source.connect(ctx.destination);
            source.start();
          }}
          className="py-2 bg-gray-600 rounded hover:bg-gray-500 text-sm font-medium col-span-2 flex items-center justify-center gap-2"
        >
          ▶️ Preview Pattern
        </button>
        <button
          onClick={() => {
            const beat = beats.generateFromPreset();
            onBeatGenerated?.(beat);
          }}
          className="py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
        >
          Add Preset
        </button>
        <button
          onClick={() => {
            const beat = beats.generateCustomBeat();
            onBeatGenerated?.(beat);
          }}
          className="py-2 bg-gray-700 rounded hover:bg-gray-600 text-sm font-medium"
        >
          Add Custom
        </button>
        <button
          onClick={() => {
            beats.generateAiPattern().then(() => {
                const beat = beats.generateCustomBeat(); // Generate audio from the new AI pattern
                onBeatGenerated?.(beat);
            });
          }}
          className="py-2 bg-gradient-to-r from-pink-500 to-purple-500 rounded hover:opacity-90 text-sm font-medium col-span-2"
        >
          ✨ AI Gen & Add
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// LOOP MACHINE COMPONENT
// =============================================================================

export const LoopMachinePanel = ({ buffer, applyEffect }) => {
  const loops = useLoopMachine(buffer, applyEffect);
  const [loopStart, setLoopStart] = useState(0);
  const [loopEnd, setLoopEnd] = useState(5);
  const [crossfade, setCrossfade] = useState(0.1);
  const [repeatCount, setRepeatCount] = useState(4);
  
  const duration = buffer ? buffer.length / buffer.sampleRate : 0;
  
  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
        🔁 Loop Machine
      </h3>
      
      {/* Loop region */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs text-gray-400">Start (s)</label>
          <input
            type="number"
            value={loopStart}
            onChange={(e) => setLoopStart(parseFloat(e.target.value) || 0)}
            className="w-full bg-gray-700 rounded px-3 py-2 text-sm"
            min={0}
            max={duration}
            step={0.1}
          />
        </div>
        <div>
          <label className="text-xs text-gray-400">End (s)</label>
          <input
            type="number"
            value={loopEnd}
            onChange={(e) => setLoopEnd(parseFloat(e.target.value) || 0)}
            className="w-full bg-gray-700 rounded px-3 py-2 text-sm"
            min={0}
            max={duration}
            step={0.1}
          />
        </div>
      </div>
      
      <EffectSlider
        label="Crossfade"
        value={crossfade}
        min={0}
        max={1}
        step={0.01}
        onChange={setCrossfade}
        format={(v) => `${(v * 1000).toFixed(0)}ms`}
      />
      
      <EffectSlider
        label="Repeat"
        value={repeatCount}
        min={1}
        max={16}
        step={1}
        onChange={setRepeatCount}
        format={(v) => `${v}x`}
      />
      
      <div className="grid grid-cols-2 gap-2 mt-4">
        <button
          onClick={() => loops.createLoopRegion(loopStart, loopEnd, `Loop ${loops.loops.length + 1}`)}
          className="py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium"
        >
          Create Loop
        </button>
        <button
          onClick={() => loops.createSeamless(loopEnd - loopStart, crossfade)}
          className="py-2 bg-gray-700 rounded hover:bg-gray-600 text-sm font-medium"
        >
          Seamless Loop
        </button>
      </div>
      
      {/* Loop list */}
      {loops.loops.length > 0 && (
        <div className="mt-4 space-y-2">
          <label className="text-xs text-gray-400">Saved Loops</label>
          {loops.loops.map(loop => (
            <div 
              key={loop.id}
              className={`flex items-center justify-between p-2 rounded ${
                loops.activeLoop === loop.id ? 'bg-purple-600/30' : 'bg-gray-700'
              }`}
            >
              <span className="text-sm">{loop.name}</span>
              <div className="flex gap-1">
                <button
                  onClick={() => loops.applyLoopToBuffer(loop.id, repeatCount)}
                  className="text-xs px-2 py-1 bg-purple-600 rounded hover:bg-purple-500"
                >
                  Apply {repeatCount}x
                </button>
                <button
                  onClick={() => loops.deleteLoop(loop.id)}
                  className="text-xs px-2 py-1 bg-red-500/50 rounded hover:bg-red-500"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// TRANSPORT BAR COMPONENT
// =============================================================================

export const TransportBar = ({ 
  isPlaying, 
  currentTime, 
  duration,
  bpm,
  onPlay, 
  onPause, 
  onStop,
  onSeek,
  onBpmChange,
  isMetronomeOn,
  onMetronomeToggle
}) => {
  return (
    <div className="bg-gray-800 border-t border-gray-700 p-3 flex items-center justify-between">
      {/* Left: Transport controls */}
      <div className="flex items-center gap-2">
        <button 
          onClick={onStop}
          className="p-2 rounded bg-gray-700 hover:bg-gray-600"
        >
          ⏹
        </button>
        <button 
          onClick={isPlaying ? onPause : onPlay}
          className={`p-3 rounded-full ${isPlaying ? 'bg-red-500' : 'bg-green-500'} hover:opacity-80`}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>
        <button 
          onClick={() => onSeek?.(0)}
          className="p-2 rounded bg-gray-700 hover:bg-gray-600"
        >
          ⏮
        </button>
      </div>
      
      {/* Center: Time display */}
      <div className="flex items-center gap-4">
        <span className="font-mono text-lg">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>
      
      {/* Right: BPM & Metronome */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">BPM</span>
          <input
            type="number"
            value={bpm}
            onChange={(e) => onBpmChange?.(parseInt(e.target.value) || 120)}
            className="w-16 bg-gray-700 rounded px-2 py-1 text-center text-sm"
            min={40}
            max={300}
          />
        </div>
        <button
          onClick={onMetronomeToggle}
          className={`p-2 rounded ${isMetronomeOn ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-400'}`}
          title="Metronome"
        >
          ⏱️
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

const EffectSlider = ({ label, value, min, max, step, onChange, format }) => (
  <div className="space-y-1">
    <div className="flex justify-between">
      <label className="text-xs text-gray-400">{label}</label>
      <span className="text-xs text-gray-300">{format ? format(value) : value.toFixed(2)}</span>
    </div>
    <input
      type="range"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
    />
  </div>
);

const formatTime = (seconds) => {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// =============================================================================
// STEM EXTRACTOR COMPONENT
// =============================================================================

export const StemExtractor = ({ onStemsExtracted }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState('');
  
  const handleExtract = async () => {
    setIsProcessing(true);
    setStatus('Uploading to ML node...');
    
    try {
        // Simulate ML processing delay
        await new Promise(r => setTimeout(r, 1500));
        setStatus('Separating vocals...');
        await new Promise(r => setTimeout(r, 1500));
        setStatus('Separating drums...');
        await new Promise(r => setTimeout(r, 1000));
        setStatus('Finalizing stems...');
        
        // In a real implementation, this would call:
        // const stems = await api.extractStems(audioFile);
        
        setIsProcessing(false);
        setStatus('Done!');
        onStemsExtracted?.();
    } catch (e) {
        setStatus('Error: ' + e.message);
        setIsProcessing(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
        🧪 AI Stem Extractor
      </h3>
      <p className="text-xs text-gray-400 mb-4">
        Separate audio into individual tracks using our ML models on Node 3.
      </p>
      
      <div className="mb-4 space-y-2">
        <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
          <input type="checkbox" className="rounded bg-gray-700 border-gray-600 text-purple-600 focus:ring-purple-500" defaultChecked />
          Auto-trim silence
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
          <input type="checkbox" className="rounded bg-gray-700 border-gray-600 text-purple-600 focus:ring-purple-500" />
          High Quality (Slower)
        </label>
      </div>

      {status && (
          <div className="mb-4 text-xs text-purple-400 font-mono">
              &gt; {status}
          </div>
      )}

      <button
        onClick={handleExtract}
        disabled={isProcessing}
        className="w-full py-2 bg-purple-600 rounded hover:bg-purple-500 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {isProcessing ? (
            <>
                <span className="animate-spin">⚙️</span> Processing...
            </>
        ) : 'Extract Stems'}
      </button>
    </div>
  );
};

// =============================================================================
// MAIN ENHANCED DAW COMPONENT
// =============================================================================

const DeviceSelector = ({ onDeviceChange }) => {
  const [devices, setDevices] = useState([]);
  const [selectedInput, setSelectedInput] = useState('');
  const [selectedOutput, setSelectedOutput] = useState('');

  useEffect(() => {
    const getDevices = async () => {
      try {
        // Request permission to list devices
        await navigator.mediaDevices.getUserMedia({ audio: true });
        const dev = await navigator.mediaDevices.enumerateDevices();
        setDevices(dev);
        
        const inputs = dev.filter(d => d.kind === 'audioinput');
        const outputs = dev.filter(d => d.kind === 'audiooutput');
        
        if (inputs.length > 0) setSelectedInput(inputs[0].deviceId);
        if (outputs.length > 0) setSelectedOutput(outputs[0].deviceId);
      } catch (err) {
        console.error('Error enumerating devices:', err);
      }
    };
    getDevices();
  }, []);

  const handleInputChange = (e) => {
    const deviceId = e.target.value;
    setSelectedInput(deviceId);
    onDeviceChange?.({ input: deviceId, output: selectedOutput });
  };

  const handleOutputChange = (e) => {
    const deviceId = e.target.value;
    setSelectedOutput(deviceId);
    onDeviceChange?.({ input: selectedInput, output: deviceId });
  };

  return (
    <div className="bg-gray-800 p-4 border-b border-gray-700 flex gap-6 items-center animate-in slide-in-from-top-2">
      <div className="flex flex-col gap-1">
        <label className="text-xs text-gray-400 font-bold uppercase">Input Device</label>
        <select 
          value={selectedInput} 
          onChange={handleInputChange}
          className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white min-w-[200px]"
        >
          {devices.filter(d => d.kind === 'audioinput').map(d => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || `Microphone ${d.deviceId.slice(0, 5)}...`}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs text-gray-400 font-bold uppercase">Output Device</label>
        <select 
          value={selectedOutput} 
          onChange={handleOutputChange}
          className="bg-gray-900 border border-gray-700 rounded px-3 py-1.5 text-sm text-white min-w-[200px]"
        >
          {devices.filter(d => d.kind === 'audiooutput').map(d => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || `Speaker ${d.deviceId.slice(0, 5)}...`}
            </option>
          ))}
        </select>
      </div>
      <div className="flex-1"></div>
      <div className="text-xs text-gray-500">
        {devices.length} devices found
      </div>
    </div>
  );
};

export const EnhancedDAWStudio = ({ onBack, onExport, onSave, initialProjectId = null }) => {
  // Core audio state
  const editor = useAudioEditor();
  const multitrack = useMultitrack();
  const recorder = useAudioRecorder();
  const playback = useAudioPlayback(editor.buffer);
  const { waveformData, peaks } = useWaveform(editor.buffer, 800);
  const processor = useEffectProcessor(editor.applyEffect);
  const { bpm } = useAudioAnalysis(editor.buffer, playback.isPlaying);
  const selection = useRegionSelection(playback.duration);
  
  // Project state (database integration)
  const [projectId, setProjectId] = useState(initialProjectId);
  const [projectName, setProjectName] = useState('Untitled Project');
  const [isProjectSaved, setIsProjectSaved] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [saveError, setSaveError] = useState(null);
  
  // UI state
  const [activePanel, setActivePanel] = useState('effects');
  const [showSettings, setShowSettings] = useState(false);
  const [showImport, setShowImport] = useState(true);
  const [showChannelWizard, setShowChannelWizard] = useState(false);
  const [showMixer, setShowMixer] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isMetronomeOn, setIsMetronomeOn] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1); // 1x to 10x
  const [recordingTrackId, setRecordingTrackId] = useState(null);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [masterVolume, setMasterVolume] = useState(1);
  const [outputDevice, setOutputDevice] = useState('default');
  const [outputDevices, setOutputDevices] = useState([]);
  const fileInputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const audioContextRef = useRef(null);
  const masterGainRef = useRef(null);
  
  // Initialize audio output system
  useEffect(() => {
    const initAudioOutput = async () => {
      try {
        // Create audio context and master gain
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = ctx;
        
        const masterGain = ctx.createGain();
        masterGain.gain.value = masterVolume;
        masterGain.connect(ctx.destination);
        masterGainRef.current = masterGain;
        
        // Enumerate output devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const outputs = devices.filter(d => d.kind === 'audiooutput');
        setOutputDevices(outputs);
      } catch (err) {
        console.error('Failed to initialize audio output:', err);
      }
    };
    
    initAudioOutput();
    
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);
  
  // Update master volume
  useEffect(() => {
    if (masterGainRef.current) {
      masterGainRef.current.gain.setValueAtTime(
        masterVolume, 
        audioContextRef.current?.currentTime || 0
      );
    }
  }, [masterVolume]);

  // Handle channel wizard result
  const handleChannelAdded = useCallback((channelData) => {
    const { buffer, name, type, midiInputId, midiInputName, instrumentId, instrumentName, instrumentParams } = channelData;
    
    // Create track with proper type information
    const trackData = {
      name,
      type: type || 'audio',
      color: type === 'midi' ? 'blue' : type === 'virtual' ? 'green' : 'purple'
    };
    
    // Add MIDI-specific data
    if (type === 'midi') {
      trackData.midiInputId = midiInputId;
      trackData.midiInputName = midiInputName;
      trackData.notes = []; // MIDI notes array
    }
    
    // Add virtual instrument data
    if (type === 'virtual') {
      trackData.instrumentId = instrumentId;
      trackData.instrumentName = instrumentName;
      trackData.instrumentParams = instrumentParams;
      trackData.notes = []; // Sequenced notes
    }
    
    // Add as new track
    if (buffer) {
      multitrack.addTrack(buffer, name);
      // Update with additional track data
      const newTrack = multitrack.tracks[multitrack.tracks.length - 1];
      if (newTrack) {
        multitrack.updateTrack(newTrack.id, trackData);
      }
    } else {
      // For MIDI/virtual, add empty track with metadata
      const track = multitrack.addTrack(null, name);
      multitrack.updateTrack(track.id, trackData);
    }
    
    // If this is the first audio, also set it as the main buffer
    if (!editor.buffer && buffer) {
      editor.setBuffer(buffer);
      setShowImport(false);
    }
  }, [multitrack, editor]);

  // Handle recording
  const handleToggleRecord = async (trackId, inputDeviceId) => {
    if (recorder.isRecording) {
      // Stop recording
      const buffer = await recorder.stopRecording();
      if (buffer) {
        multitrack.updateTrack(trackId, { buffer });
      }
      setRecordingTrackId(null);
      playback.stop();
    } else {
      // Start recording
      if (!inputDeviceId) {
        alert('Please select an input device first');
        return;
      }
      
      try {
        recorder.setSelectedDeviceId(inputDeviceId);
        setRecordingTrackId(trackId);
        
        // Start playback of other tracks for monitoring
        // TODO: Create a mix of other tracks and play it
        // For now, just start recording
        await recorder.startRecording();
        
        // If we have other tracks, we should play them
        // This requires a more complex playback engine that can play the mix
        // For now, let's just assume we want to record
      } catch (err) {
        console.error('Failed to start recording:', err);
        setRecordingTrackId(null);
      }
    }
  };

  // Handle zoom
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev * 1.5, 10));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev / 1.5, 1));
  
  // Handle publish to music feed
  const handlePublishToMusicFeed = async (publishData, mediaData) => {
    setIsExporting(true);
    setSaveError(null);
    
    try {
      // Get final buffer to export
      let finalBuffer = editor.buffer;
      
      if (multitrack.tracks.length > 0 && multitrack.tracks.some(t => t.buffer)) {
        const trackWithAudio = multitrack.tracks.find(t => t.buffer);
        if (trackWithAudio) {
          finalBuffer = trackWithAudio.buffer;
        }
      }
      
      if (!finalBuffer) {
        throw new Error('No audio to export');
      }
      
      // Compress audio
      setUploadProgress({ stage: 'compressing', progress: 0 });
      
      const compressed = await audioUploadService.compressAudioBuffer(finalBuffer, {
        format: 'wav',
        quality: 0.9,
        sampleRate: 44100
      });
      
      setUploadProgress({ stage: 'uploading', progress: 0.3 });
      
      // Upload to server
      const uploadResult = await audioUploadService.uploadAudio(compressed, {
        title: publishData.title || projectName,
        description: publishData.description,
        duration: finalBuffer.duration,
        is_public: publishData.visibility === 'public',
        project_id: projectId
      }, setUploadProgress);
      
      setUploadProgress({ stage: 'publishing', progress: 0.6 });
      
      // Publish to music feed
      const trackData = {
        title: publishData.title || projectName,
        description: publishData.description,
        genre: publishData.genre,
        tags: publishData.tags,
        audio_url: uploadResult.url,
        cover_art_url: uploadResult.waveform_url, // Use waveform as cover if no custom art
        bpm: publishData.bpm || bpm,
        key: publishData.key,
        duration_ms: Math.round(finalBuffer.duration * 1000),
        source_type: 'daw',
        daw_project_id: projectId?.toString(),
        community_id: publishData.community_id,
        visibility: publishData.visibility,
        allow_use_in_videos: publishData.allow_use_in_videos !== false,
        allow_remix: publishData.allow_remix || false,
        license_type: 'all_rights_reserved',
        is_monetized: publishData.is_monetized || false,
        price_cents: publishData.price_cents || 0,
        is_downloadable: publishData.is_downloadable || false,
      };
      
      const trackResponse = await api.publishMusicTrack(trackData);
      
      setUploadProgress({ stage: 'creating_post', progress: 0.8 });
      
      // Also create a post to appear in feeds
      if (publishData.visibility === 'public') {
        try {
          await api.createCrossMediaPost({
            caption: publishData.description || publishData.title,
            hashtags: publishData.tags,
            visibility: publishData.visibility,
            content_type: 'audio',
            feed_type: 'music',
            source_type: 'daw',
            audio_url: uploadResult.url,
            thumbnail_url: uploadResult.waveform_url,
            community_id: publishData.community_id,
            audio_metadata: {
              track_id: trackResponse.id,
              bpm: publishData.bpm || bpm,
              key: publishData.key,
              genre: publishData.genre,
              duration_ms: Math.round(finalBuffer.duration * 1000),
            },
          });
        } catch (postError) {
          console.warn('Feed post creation failed, but track was published:', postError);
        }
      }
      
      setUploadProgress({ stage: 'complete', progress: 1 });
      setIsProjectSaved(true);
      
      // Call onExport callback if provided
      if (onExport) {
        const file = new File([compressed], `${projectName}.wav`, { type: 'audio/wav' });
        onExport(file);
      }
      
      setTimeout(() => setUploadProgress(null), 2000);
      
    } catch (err) {
      console.error('Publish failed:', err);
      setSaveError(err.message);
      setUploadProgress({ stage: 'error', error: err.message });
      throw err;
    } finally {
      setIsExporting(false);
    }
  };
  
  // Handle export for integration with Upload flow (legacy - opens publish modal now)
  const handleExportForUpload = async () => {
    if (!editor.buffer && multitrack.tracks.length === 0) return;
    setIsExporting(true);
    setSaveError(null);
    
    try {
      // If we have multitrack, mix them down first
      let finalBuffer = editor.buffer;
      
      if (multitrack.tracks.length > 0 && multitrack.tracks.some(t => t.buffer)) {
        // TODO: Mix down all tracks into one buffer
        // For now, use the first track with audio
        const trackWithAudio = multitrack.tracks.find(t => t.buffer);
        if (trackWithAudio) {
          finalBuffer = trackWithAudio.buffer;
        }
      }
      
      if (!finalBuffer) {
        throw new Error('No audio to export');
      }
      
      // Compress and upload
      setUploadProgress({ stage: 'compressing', progress: 0 });
      
      const compressed = await audioUploadService.compressAudioBuffer(finalBuffer, {
        format: 'wav',
        quality: 0.9,
        sampleRate: 44100
      });
      
      setUploadProgress({ stage: 'uploading', progress: 0.5 });
      
      // Upload to server
      const result = await audioUploadService.uploadAudio(compressed, {
        title: projectName || `Audio Export ${Date.now()}`,
        description: `Exported from DAW Studio`,
        duration: finalBuffer.duration,
        is_public: false,
        project_id: projectId
      }, setUploadProgress);
      
      console.log('✅ Audio exported and uploaded:', result);
      setUploadProgress({ stage: 'complete', progress: 1 });
      
      // Also call the onExport callback if provided
      if (onExport) {
        const file = new File([compressed], `${projectName}.wav`, { type: 'audio/wav' });
        onExport(file);
      }
      
      setTimeout(() => setUploadProgress(null), 2000);
      
    } catch (err) {
      console.error('Export failed:', err);
      setSaveError(err.message);
      setUploadProgress({ stage: 'error', error: err.message });
    } finally {
      setIsExporting(false);
    }
  };

  // Handle save project to database
  const handleSaveProject = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // Create project if it doesn't exist
      if (!projectId) {
        setUploadProgress({ stage: 'creating', progress: 0 });
        
        const newProject = await audioUploadService.createDAWProject({
          name: projectName,
          description: 'Created in DAW Studio',
          bpm: bpm || 120,
          time_signature: '4/4',
          is_public: false
        });
        
        setProjectId(newProject.id);
        console.log('✅ Project created:', newProject.id);
      }
      
      // Save project state (tracks, settings)
      setUploadProgress({ stage: 'saving', progress: 0.3 });
      
      await audioUploadService.saveDAWProjectState(projectId, {
        tracks: multitrack.tracks,
        bpm: bpm || 120,
        masterVolume,
        duration: playback.duration || 60
      });
      
      // Upload audio for each track that has a buffer but no asset_id
      let trackIndex = 0;
      for (const track of multitrack.tracks) {
        if (track.buffer && !track.assetId) {
          setUploadProgress({ 
            stage: 'uploading_tracks', 
            progress: 0.3 + (0.6 * (trackIndex / multitrack.tracks.length)),
            currentTrack: track.name
          });
          
          const compressed = await audioUploadService.compressAudioBuffer(track.buffer, {
            format: 'wav',
            sampleRate: 44100
          });
          
          const asset = await audioUploadService.uploadProjectAudio(projectId, compressed, {
            name: track.name,
            asset_type: 'audio'
          });
          
          // Update track with asset ID
          multitrack.updateTrack(track.id, { assetId: asset.id });
        }
        trackIndex++;
      }
      
      setUploadProgress({ stage: 'complete', progress: 1 });
      setIsProjectSaved(true);
      
      console.log('✅ Project saved successfully');
      
      // Call onSave callback if provided
      if (onSave) {
        const wavBlob = editor.getWavBlob?.();
        if (wavBlob) {
          const file = new File([wavBlob], `${projectName}.wav`, { type: 'audio/wav' });
          await onSave(file);
        }
      }
      
      setTimeout(() => setUploadProgress(null), 2000);
      
    } catch (err) {
      console.error('Save failed:', err);
      setSaveError(err.message);
      setUploadProgress({ stage: 'error', error: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save to profile (legacy - use handleSaveProject instead)
  const handleSave = async () => {
    await handleSaveProject();
  };
  
  // Keyboard shortcuts
  useDAWKeyboardShortcuts({
    onPlay: playback.togglePlayPause,
    onStop: playback.stop,
    onUndo: editor.undo,
    onRedo: editor.redo,
    onSave: () => editor.exportWav('audio-export.wav')
  });
  
  // Handle file import
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await editor.loadFromFile(file);
      setShowImport(false);
    }
  };
  
  // Handle beat generation
  const handleBeatGenerated = async (beatBuffer) => {
    // Ensure audio context is running
    await ensureAudioContextRunning();

    if (!editor.buffer) {
      editor.setBuffer(beatBuffer);
      setShowImport(false);
    } else {
      // Mix with existing audio
      editor.applyEffect((buf) => {
        return mixBuffers(buf, beatBuffer, 0.3);
      }, 'add beat');
    }
  };
  
  return (
    <div className="h-full bg-gray-900 flex flex-col text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center hover:bg-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          {/* Project Name (editable) */}
          <div className="flex items-center gap-2">
            <span className="text-lg">🎵</span>
            <input
              type="text"
              value={projectName}
              onChange={(e) => { setProjectName(e.target.value); setIsProjectSaved(false); }}
              className="bg-transparent text-lg font-bold border-b border-transparent hover:border-gray-600 focus:border-purple-500 focus:outline-none px-1"
              placeholder="Untitled Project"
            />
            {isProjectSaved && <span className="text-green-400 text-xs">✓ Saved</span>}
            {!isProjectSaved && multitrack.tracks.length > 0 && <span className="text-yellow-400 text-xs">• Unsaved</span>}
          </div>
          
          {/* Status indicators */}
          {(editor.buffer || multitrack.tracks.some(t => t.buffer)) && (
            <div className="flex items-center gap-3 ml-4 text-xs text-gray-400">
              <span>📊 RMS: {(peaks.rms * 100).toFixed(1)}%</span>
              <span>📈 Peak: {(peaks.peak * 100).toFixed(1)}%</span>
              {bpm && <span>🎵 BPM: {bpm.toFixed(0)}</span>}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowMixer(!showMixer)}
            className={`px-3 py-1.5 rounded text-sm font-bold ${showMixer ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            title="Toggle Mixer Panel"
          >
            🎚️ Mixer
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`px-3 py-1.5 rounded text-sm font-bold ${showSettings ? 'bg-gray-600' : 'bg-gray-700 hover:bg-gray-600'}`}
          >
            ⚙️ Settings
          </button>
          <button
            className="px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded text-sm font-bold animate-pulse"
            onClick={() => alert('Going live... (Feature coming soon)')}
          >
            🔴 Go Live
          </button>
          <div className="h-6 w-px bg-gray-700 mx-2" />
          <button
            onClick={editor.undo}
            disabled={!editor.canUndo}
            className={`px-3 py-1.5 rounded text-sm ${editor.canUndo ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-800 text-gray-600'}`}
          >
            ↩ Undo
          </button>
          <button
            onClick={editor.redo}
            disabled={!editor.canRedo}
            className={`px-3 py-1.5 rounded text-sm ${editor.canRedo ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-800 text-gray-600'}`}
          >
            ↪ Redo
          </button>
          
          {/* Save to Cloud/DB */}
          <button
            onClick={handleSaveProject}
            disabled={isSaving || (multitrack.tracks.length === 0 && !editor.buffer)}
            className="px-4 py-1.5 bg-purple-600 rounded text-sm hover:bg-purple-500 font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <span className="animate-spin">⏳</span>
                Saving...
              </>
            ) : (
              <>💾 Save Project</>
            )}
          </button>
          
          {/* Local Export */}
          <button
            onClick={() => editor.exportWav(`${projectName || 'audio-export'}.wav`)}
            disabled={!editor.buffer && !multitrack.tracks.some(t => t.buffer)}
            className="px-4 py-1.5 bg-blue-600 rounded text-sm hover:bg-blue-500 font-medium disabled:opacity-50"
          >
            ⬇️ Export WAV
          </button>
          
          {/* Upload to Platform */}
          <button
            onClick={() => setShowPublishModal(true)}
            disabled={isExporting || (!editor.buffer && !multitrack.tracks.some(t => t.buffer))}
            className="px-4 py-1.5 bg-emerald-600 rounded text-sm hover:bg-emerald-500 font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {isExporting ? (
              <>
                <span className="animate-spin">⏳</span>
                Publishing...
              </>
            ) : (
              <>🎵 Publish Track</>
            )}
          </button>
        </div>
      </div>
      
      {/* Upload Progress Bar */}
      {uploadProgress && (
        <div className="bg-gray-800 border-b border-gray-700 px-4 py-2">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-300">
                  {uploadProgress.stage === 'compressing' && '🔄 Compressing audio...'}
                  {uploadProgress.stage === 'uploading' && '📤 Uploading to server...'}
                  {uploadProgress.stage === 'creating' && '📁 Creating project...'}
                  {uploadProgress.stage === 'saving' && '💾 Saving project state...'}
                  {uploadProgress.stage === 'uploading_tracks' && `📤 Uploading: ${uploadProgress.currentTrack}`}
                  {uploadProgress.stage === 'complete' && '✅ Complete!'}
                  {uploadProgress.stage === 'error' && `❌ Error: ${uploadProgress.error}`}
                </span>
                <span className="text-xs text-gray-400">
                  {Math.round((uploadProgress.progress || 0) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-300 ${
                    uploadProgress.stage === 'error' ? 'bg-red-500' :
                    uploadProgress.stage === 'complete' ? 'bg-green-500' :
                    'bg-purple-500'
                  }`}
                  style={{ width: `${(uploadProgress.progress || 0) * 100}%` }}
                />
              </div>
            </div>
            {uploadProgress.stage !== 'complete' && uploadProgress.stage !== 'error' && (
              <button
                onClick={() => setUploadProgress(null)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Error Display */}
      {saveError && !uploadProgress && (
        <div className="bg-red-500/10 border-b border-red-500/50 px-4 py-2 flex items-center justify-between">
          <span className="text-red-400 text-sm">❌ {saveError}</span>
          <button onClick={() => setSaveError(null)} className="text-red-400 hover:text-red-300">✕</button>
        </div>
      )}

      {/* Device Settings */}
      {showSettings && (
        <DeviceSelector 
          onDeviceChange={(devices) => console.log('Devices changed:', devices)} 
        />
      )}

      {/* Main workspace */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar - Panel selector */}
        <div className="w-16 bg-gray-800 border-r border-gray-700 flex flex-col">
          {[
            { id: 'effects', icon: '🎛️', label: 'Effects' },
            { id: 'beats', icon: '🥁', label: 'Beats' },
            { id: 'loops', icon: '🔁', label: 'Loops' },
            { id: 'stems', icon: '🧪', label: 'Stems' },
            { id: 'collab', icon: '👥', label: 'Collab' },
            { id: 'chat', icon: '💬', label: 'Chat' }
          ].map(panel => (
            <button
              key={panel.id}
              onClick={() => setActivePanel(panel.id)}
              className={`p-3 flex flex-col items-center gap-1 border-b border-gray-700 ${
                activePanel === panel.id ? 'bg-purple-600' : 'hover:bg-gray-700'
              }`}
            >
              <span className="text-xl">{panel.icon}</span>
              <span className="text-xs">{panel.label}</span>
            </button>
          ))}
        </div>
        
        {/* Tool panel */}
        <div className="w-80 bg-gray-850 border-r border-gray-700 overflow-y-auto p-4 flex-shrink-0 hidden md:block">
          {activePanel === 'effects' && (
            <EffectRack processor={processor} />
          )}
          {activePanel === 'beats' && (
            <BeatMachine onBeatGenerated={handleBeatGenerated} />
          )}
          {activePanel === 'loops' && (
            <LoopMachinePanel buffer={editor.buffer} applyEffect={editor.applyEffect} />
          )}
          {activePanel === 'stems' && (
            <StemExtractor onStemsExtracted={() => console.log('Stems extracted')} />
          )}
          {activePanel === 'collab' && (
            <div className="space-y-4">
                <h3 className="font-bold text-lg">Collaborators</h3>
                <div className="bg-gray-800 p-3 rounded">
                    <p className="text-sm text-gray-400 mb-2">Invite friends to this session</p>
                    <button className="w-full py-2 bg-purple-600 rounded text-sm font-bold">
                        + Invite Friend
                    </button>
                </div>
                <div className="space-y-2">
                    <div className="flex items-center gap-2 p-2 bg-gray-800 rounded">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">You</div>
                        <span className="text-sm">Owner (Online)</span>
                    </div>
                </div>
            </div>
          )}
          {activePanel === 'chat' && (
            <div className="flex flex-col h-full">
                <h3 className="font-bold text-lg mb-4">Project Chat</h3>
                <div className="flex-1 bg-gray-800 rounded p-2 mb-2 overflow-y-auto">
                    <div className="text-center text-gray-500 text-sm mt-4">No messages yet</div>
                </div>
                <div className="flex gap-2">
                    <input 
                        type="text" 
                        placeholder="Type a message..." 
                        className="flex-1 bg-gray-700 rounded px-2 py-1 text-sm"
                    />
                    <button className="px-3 bg-purple-600 rounded">➤</button>
                </div>
            </div>
          )}
        </div>

        {/* Main content area - Timeline View */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {showImport && !editor.buffer && multitrack.tracks.length === 0 ? (
            <div className="flex-1 flex items-center justify-center bg-gray-900">
              <div 
                className="w-full max-w-lg p-8 border-2 border-dashed border-gray-600 rounded-xl text-center cursor-pointer hover:border-purple-500 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="text-5xl mb-4">🎵</div>
                <p className="text-xl font-bold mb-2">Start Your Project</p>
                <p className="text-sm text-gray-400 mb-4">Drop audio file here or click to browse</p>
                <p className="text-xs text-gray-500 mb-6">Supports WAV, MP3, OGG, FLAC</p>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowChannelWizard(true); }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm font-bold"
                  >
                    + Add Channel
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); multitrack.addTrack(null, `Track 1`); setShowImport(false); }}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-bold"
                  >
                    + Empty Track
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>
          ) : (
            <>
              {/* DAW Timeline - The main multitrack view */}
              <DAWTimeline
                tracks={multitrack.tracks}
                duration={playback.duration || 60}
                currentTime={playback.currentTime}
                isPlaying={playback.isPlaying}
                bpm={bpm || 120}
                armedTrackIds={recordingTrackId ? [recordingTrackId] : []}
                selectedTrackId={selectedTrackId}
                recordingTrackId={recordingTrackId}
                recordingWaveform={recorder.waveformData || []}
                onSeek={playback.seek}
                onTrackSelect={setSelectedTrackId}
                onTrackMute={multitrack.toggleMute}
                onTrackSolo={multitrack.toggleSolo}
                onTrackArm={(trackId) => handleToggleRecord(trackId, multitrack.tracks.find(t => t.id === trackId)?.inputDeviceId)}
                onTrackVolumeChange={multitrack.setTrackVolume}
                onTrackPanChange={(trackId, pan) => multitrack.updateTrack(trackId, { pan })}
                onTrackNameChange={(trackId, name) => multitrack.updateTrack(trackId, { name })}
                onTrackColorChange={(trackId, color) => multitrack.updateTrack(trackId, { color })}
                onTrackDelete={multitrack.removeTrack}
                onRegionSelect={(trackId, region) => multitrack.updateTrack(trackId, { selection: region })}
                onAddTrack={() => setShowChannelWizard(true)}
              />
              
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </>
          )}
          
          {/* Processing indicator */}
          {editor.isProcessing && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-gray-800 rounded-lg p-4 flex items-center gap-3">
                <div className="animate-spin w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full" />
                <span>Processing...</span>
              </div>
            </div>
          )}
          
          {/* Error display */}
          {editor.error && (
            <div className="absolute bottom-4 left-4 right-4 p-2 bg-red-500/20 border border-red-500 rounded text-red-400 text-sm z-50">
              {editor.error}
            </div>
          )}
        </div>
      </div>

      {/* Mixer Panel */}
      {showMixer && multitrack.tracks.length > 0 && (
        <DAWMixer
          tracks={multitrack.tracks}
          masterVolume={masterVolume}
          selectedTrackId={selectedTrackId}
          onTrackSelect={setSelectedTrackId}
          onTrackMute={multitrack.toggleMute}
          onTrackSolo={multitrack.toggleSolo}
          onTrackArm={(trackId) => handleToggleRecord(trackId, multitrack.tracks.find(t => t.id === trackId)?.inputDeviceId)}
          onTrackVolumeChange={multitrack.setTrackVolume}
          onTrackPanChange={(trackId, pan) => multitrack.updateTrack(trackId, { pan })}
          onMasterVolumeChange={setMasterVolume}
          trackLevels={{}}
          masterLevelL={peaks?.peak || 0}
          masterLevelR={peaks?.peak || 0}
        />
      )}

      {/* Transport bar */}
      <TransportBar
        isPlaying={playback.isPlaying}
        currentTime={playback.currentTime}
        duration={playback.duration}
        bpm={bpm || 120}
        onPlay={playback.play}
        onPause={playback.pause}
        onStop={playback.stop}
        onSeek={playback.seek}
        onBpmChange={() => {}}
        isMetronomeOn={isMetronomeOn}
        onMetronomeToggle={() => setIsMetronomeOn(!isMetronomeOn)}
      />
      
      {/* Master Output Controls */}
      <div className="bg-gray-800 border-t border-gray-700 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-xs text-gray-400 font-medium">Master Output</span>
          
          {/* Volume Slider */}
          <div className="flex items-center gap-2">
            <span className="text-xs">🔊</span>
            <input
              type="range"
              min="0"
              max="2"
              step="0.01"
              value={masterVolume}
              onChange={(e) => setMasterVolume(parseFloat(e.target.value))}
              className="w-24 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-xs text-gray-400 w-10">{Math.round(masterVolume * 100)}%</span>
          </div>
          
          {/* Output Device Selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs">🎧</span>
            <select
              value={outputDevice}
              onChange={(e) => setOutputDevice(e.target.value)}
              className="bg-gray-700 text-xs rounded px-2 py-1 border border-gray-600 focus:outline-none focus:border-purple-500"
            >
              <option value="default">Default Output</option>
              {outputDevices.map(device => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Output ${device.deviceId.slice(0, 8)}`}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* VU Meter Placeholder */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">L</span>
          <div className="w-20 h-2 bg-gray-700 rounded overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 transition-all"
              style={{ width: `${(peaks?.peak || 0) * 100}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">R</span>
        </div>
      </div>
      
      {/* Channel Wizard Modal */}
      <AddChannelWizard
        isOpen={showChannelWizard}
        onClose={() => setShowChannelWizard(false)}
        onChannelAdded={handleChannelAdded}
      />
      
      {/* Publish to Music Feed Modal */}
      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onPublish={handlePublishToMusicFeed}
        contentType="music"
        initialTitle={projectName}
        initialDescription=""
        initialTags={[]}
        mediaData={{
          buffer: editor.buffer,
          duration: playback.duration,
          bpm: bpm,
        }}
      />
    </div>
  );
};

export default EnhancedDAWStudio;
